import { useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import StarIcon from '@mui/icons-material/Star';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { arrangeProducts, getOpenProduct } from './arrange-products';
import { arrangeShortcuts } from './arrange-shortcuts';
import { ProductBadge } from './product-badge';
import { ProductRow } from './product-row';
import { JobRow } from './job-row';
import { CategoryLegend } from './category-legend';
import { visuallyHiddenSx } from '../layout/visually-hidden';
import type { DragEndEvent } from '@dnd-kit/core';
import type { ReactNode } from 'react';
import type { SearchMode } from './product-switcher-context';
import type { Organization, OrganizationSelection } from './types';
import type {
  ProductDirectoryEntry,
  ProductDirectoryJob,
  ProductDirectoryResponse,
  ProductDirectoryShortcut,
} from './product-directory-api';

type ProductDirectoryProps = {
  directory: ProductDirectoryResponse | null;
  hasFailed?: boolean;
  /** The one query, typed in the context bar. */
  query?: string;
  /** Which list the bar is searching; the other one is not shown. */
  mode?: SearchMode;
  organizations?: Organization[];
  selectedOrganization?: OrganizationSelection | null;
  onOrganizationChange?: (organization: OrganizationSelection) => void | Promise<void>;
  /** Turns the organization the footer names into the way into the organization list. */
  onSearchOrganizations?: () => void;
  favorites: string[];
  onToggleFavorite: (product: string) => void;
  /** The whole band in its new order, so the member's arrangement is what gets stored. */
  onReorderFavorites: (products: string[]) => void;
  onLaunch: (product: ProductDirectoryEntry) => void;
  /** 'panel' is the edge-to-edge fold-down; 'page' sits inside a PageContainer that owns the gutter. */
  variant?: 'panel' | 'page';
};

type ShortcutChipRowProps = {
  shortcuts: ProductDirectoryShortcut[];
  onToggleFavorite: (token: string) => void;
  onReorderFavorites: (tokens: string[]) => void;
};

type ShortcutChipProps = {
  shortcut: ProductDirectoryShortcut;
  /** One shortcut has nowhere to go, so it carries no handle to drag it by. */
  canReorder: boolean;
  onToggleFavorite: (token: string) => void;
};

function ShortcutChip({ shortcut, canReorder, onToggleFavorite }: ShortcutChipProps) {
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({
    id: shortcut.token,
  });

  // Lifted over its neighbours while it travels, so it slides across them rather than under.
  let opacity = 1;
  let zIndex = 'auto';
  if (isDragging) {
    opacity = 0.4;
    zIndex = '1';
  }

  // The launch link owns the right edge when there is no handle behind it.
  let launchRight = 1.5;
  if (canReorder) {
    launchRight = 0.75;
  }

  let handle: ReactNode = null;
  if (canReorder) {
    handle = (
      <IconButton
        size="small"
        data-reorder-handle
        aria-label={`Reorder ${shortcut.label}`}
        {...attributes}
        {...listeners}
        sx={{
          mr: 0.25,
          color: 'text.secondary',
          cursor: 'grab',
          touchAction: 'none',
          opacity: 0,
          transition: 'opacity 150ms',
          '&:active': { cursor: 'grabbing' },
          '&:focus-visible': { opacity: 1, outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' },
          '@media (hover: none)': { opacity: 1 },
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        <DragIndicatorIcon sx={{ fontSize: 16 }} aria-hidden="true" />
      </IconButton>
    );
  }

  // An action shortcut says which product it opens, since its label names the work, not the app.
  let title: string | undefined = undefined;
  if (shortcut.kind === 'action') {
    title = shortcut.label;
  }

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform) }}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
        opacity: opacity,
        zIndex: zIndex,
        transition: transition,
        '&:hover': { borderColor: 'primary.main' },
        '&:focus-within': { borderColor: 'primary.main' },
        // The handle is state worth seeing only once a pointer or the keyboard has arrived.
        '&:hover [data-reorder-handle]': { opacity: 1 },
        '&:focus-within [data-reorder-handle]': { opacity: 1 },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      <IconButton
        size="small"
        aria-label={`Favorite ${shortcut.label}`}
        aria-pressed
        onClick={() => onToggleFavorite(shortcut.token)}
        sx={{
          ml: 0.25,
          color: 'primary.main',
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' },
        }}
      >
        <StarIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Box
        component="a"
        href={shortcut.url ?? undefined}
        title={title}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pl: 0.5,
          pr: launchRight,
          py: 0.75,
          color: 'inherit',
          textDecoration: 'none',
          '&:hover': { bgcolor: 'action.hover' },
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' },
        }}
      >
        <ProductBadge badge={shortcut.badge} category={shortcut.category} size={22} />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {shortcut.label}
        </Typography>
      </Box>
      {handle}
    </Box>
  );
}

function ShortcutChipRow({ shortcuts, onToggleFavorite, onReorderFavorites }: ShortcutChipRowProps) {
  // A few pixels of travel before a drag begins, so a plain click still opens the shortcut.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const tokens = shortcuts.map((shortcut) => shortcut.token);
  const canReorder = shortcuts.length > 1;

  function getLabel(id: string | number): string {
    let label = String(id);
    const found = shortcuts.find((shortcut) => shortcut.token === String(id));
    if (found !== undefined) {
      label = found.label;
    }
    return label;
  }

  const announcements = {
    onDragStart: ({ active }: { active: { id: string | number } }) => `Picked up ${getLabel(active.id)}.`,
    onDragOver: ({ active }: { active: { id: string | number } }) => `Moving ${getLabel(active.id)}.`,
    onDragEnd: ({ active }: { active: { id: string | number } }) => `${getLabel(active.id)} dropped.`,
    onDragCancel: ({ active }: { active: { id: string | number } }) => `Reordering ${getLabel(active.id)} cancelled.`,
  };

  function handleDragEnd(event: DragEndEvent) {
    const draggedId = String(event.active.id);
    let overId = draggedId;
    if (event.over !== null && event.over !== undefined) {
      overId = String(event.over.id);
    }
    if (draggedId !== overId) {
      const from = tokens.indexOf(draggedId);
      const to = tokens.indexOf(overId);
      if (from >= 0 && to >= 0) {
        onReorderFavorites(arrayMove(tokens, from, to));
      }
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      accessibility={{ announcements: announcements }}
    >
      <SortableContext items={tokens} strategy={rectSortingStrategy}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {shortcuts.map((shortcut) => (
            <ShortcutChip
              key={shortcut.token}
              shortcut={shortcut}
              canReorder={canReorder}
              onToggleFavorite={onToggleFavorite}
            />
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
}

type JobsSectionProps = {
  jobs: ProductDirectoryJob[];
  productLabel: string;
  productBadge: string;
  productCategory: string;
  favorites: string[];
  onToggleFavorite: (token: string) => void;
  contentSx: Record<string, unknown>;
  headingLevel: 'h2' | 'h3';
  query: string;
  legend: ReactNode;
};

/**
 * The work the product you are in exists to get done, read in the order its own left
 * navigation lists those destinations. Only the open product's actions appear: the switcher is
 * the way between products, not a directory of everything all of them can do.
 */
function JobsSection({
  jobs,
  productLabel,
  productBadge,
  productCategory,
  favorites,
  onToggleFavorite,
  contentSx,
  headingLevel,
  query,
  legend,
}: JobsSectionProps) {
  const needle = query.trim().toLowerCase();
  let visible = jobs;
  if (needle.length > 0) {
    visible = jobs.filter((job) => job.label.toLowerCase().includes(needle));
  }

  let body: ReactNode = (
    <Typography variant="body2" sx={{ color: 'text.secondary' }} role="status">
      {`No actions match \u201c${query}\u201d.`}
    </Typography>
  );
  if (visible.length > 0) {
    body = (
      <Box
        component="ul"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
          columnGap: 3,
          rowGap: 0.5,
          listStyle: 'none',
          m: 0,
          p: 0,
        }}
      >
        {visible.map((job) => (
          <JobRow
            key={job.token}
            job={job}
            badge={productBadge}
            category={productCategory}
            isFavorite={favorites.includes(job.token)}
            onToggleFavorite={onToggleFavorite}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box
      component="section"
      aria-label={`Actions in ${productLabel}`}
      sx={{ pt: 2, pb: 2.5, borderTop: '1px solid', borderColor: 'divider' }}
    >
      <Box sx={contentSx}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', columnGap: 3, rowGap: 1, flexWrap: 'wrap', mb: 1 }}
        >
          <Typography variant="caption" component={headingLevel} sx={{ color: 'text.secondary', mr: 'auto' }}>
            Actions
          </Typography>
          {legend}
        </Box>
        {body}
      </Box>
    </Box>
  );
}

function getOpenProductLabel(products: ProductDirectoryEntry[], current: string): string {
  let label = current;
  const open = getOpenProduct(products, current);
  if (open !== undefined) {
    label = open.label;
  }
  return label;
}

function getOrganizationBadge(name: string): string {
  const words = name.trim().split(/\s+/).filter((word) => word.length > 0);
  if (words.length === 0) {
    return '??';
  }
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }
  return (words[0][0] + words[1][0]).toUpperCase();
}

type OrganizationRowProps = {
  organization: Organization;
  isSelected: boolean;
  hasCopied: boolean;
  onSelect: (organization: OrganizationSelection) => void | Promise<void>;
  onCopy: (organization: Organization) => void;
  contentSx: Record<string, unknown>;
};

function OrganizationRow({ organization, isSelected, hasCopied, onSelect, onCopy, contentSx }: OrganizationRowProps) {
  function handleCopy() {
    onCopy(organization);
  }

  let copyLabel = `Copy ${organization.name}`;
  let copyIcon = <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} aria-hidden="true" />;
  if (hasCopied) {
    copyLabel = `Copied ${organization.name}`;
    copyIcon = <CheckOutlinedIcon sx={{ fontSize: 16 }} aria-hidden="true" />;
  }

  return (
    <Box
      sx={[
        contentSx,
        {
          position: 'relative',
          // Offered on approach, like the star on a product row.
          '&:hover [data-copy-organization]': { opacity: 1 },
          '&:focus-within [data-copy-organization]': { opacity: 1 },
        },
      ]}
    >
      <ButtonBase
        data-organization-row
        onClick={() => onSelect(organization)}
        aria-current={isSelected ? 'true' : undefined}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          justifyContent: 'flex-start',
          textAlign: 'left',
          mx: -1,
          pl: 1,
          pr: 5,
          py: 1,
          borderLeft: '3px solid transparent',
          borderLeftColor: isSelected ? 'primary.main' : 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' },
        }}
      >
        <ProductBadge
          badge={getOrganizationBadge(organization.name)}
          category="platform"
          color="var(--color-muted)"
          size={22}
        />
        <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 400 }}>
          {organization.name}
        </Typography>
      </ButtonBase>
      {/* A sibling of the row rather than a child: a button inside a button is neither valid
          nor reachable, and a click here must not switch the organization. */}
      <IconButton
        size="small"
        data-copy-organization
        aria-label={copyLabel}
        onClick={handleCopy}
        sx={{
          position: 'absolute',
          top: '50%',
          right: 0,
          transform: 'translateY(-50%)',
          zIndex: 1,
          color: 'text.secondary',
          opacity: 0,
          transition: 'opacity 150ms',
          '&:focus-visible': { opacity: 1, outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' },
          '@media (hover: none)': { opacity: 1 },
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        {copyIcon}
      </IconButton>
    </Box>
  );
}

type OrganizationListSectionProps = {
  organizations: Organization[];
  selected?: OrganizationSelection | null;
  onSelect: (organization: OrganizationSelection) => void | Promise<void>;
  contentSx: Record<string, unknown>;
  query: string;
};

function OrganizationListSection({ organizations, selected, onSelect, contentSx, query }: OrganizationListSectionProps) {
  const [copiedName, setCopiedName] = useState<string | null>(null);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    return () => {
      clearTimeout(resetTimer.current);
    };
  }, []);

  async function handleCopy(organization: Organization) {
    try {
      await navigator.clipboard.writeText(organization.name);
      setCopiedName(organization.name);
      clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => {
        setCopiedName(null);
      }, 1500);
    } catch {} // Clipboard unavailable or refused — the name is still on screen to read
  }

  return (
    <Box
      component="section"
      aria-label="Organizations"
      sx={{ pt: 1, pb: 1, borderTop: '1px solid', borderColor: 'divider' }}
    >
      {organizations.length === 0 ? (
        <Box sx={contentSx}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} role="status">
            {`No organizations match “${query}”.`}
          </Typography>
        </Box>
      ) : (
        // Tall enough to scan a real membership list, but bounded by the viewport so the panel
        // never grows past the screen on a short window.
        <Box sx={{ maxHeight: 'min(52vh, 420px)', overflowY: 'auto' }}>
          {organizations.map((organization) => {
            const isSelected =
              selected !== null &&
              selected !== undefined &&
              selected.name === organization.name &&
              selected.precedence === organization.precedence;
            return (
              <OrganizationRow
                key={`${organization.precedence}:${organization.name}`}
                organization={organization}
                isSelected={isSelected}
                hasCopied={copiedName === organization.name}
                onSelect={onSelect}
                onCopy={handleCopy}
                contentSx={contentSx}
              />
            );
          })}
        </Box>
      )}
      {/* Copying answers nothing on screen a screen reader can reach, so the result is said. */}
      <Box component="p" role="status" sx={visuallyHiddenSx}>
        {copiedName === null ? '' : `Copied ${copiedName}`}
      </Box>
    </Box>
  );
}

export function ProductDirectory({
  directory,
  hasFailed = false,
  favorites,
  onToggleFavorite,
  onReorderFavorites,
  onLaunch,
  variant = 'panel',
  query = '',
  mode = 'products',
  organizations = [],
  selectedOrganization,
  onOrganizationChange,
  onSearchOrganizations,
}: ProductDirectoryProps) {
  const isPanel = variant === 'panel';
  // The panel heads its own list, so its rows sit under that h3. The page has only its own h1
  // above them, and jumping h1 -> h4 (then back to h3 in the footer) is a broken outline.
  let rowHeading: 'h2' | 'h4' = 'h2';
  let bandHeading: 'h2' | 'h3' = 'h2';
  if (isPanel) {
    rowHeading = 'h4';
    bandHeading = 'h3';
  }
  // The card owns its own width now, so the gutter is plain padding rather than a container.
  let contentSx: Record<string, unknown> = { width: '100%' };
  if (isPanel) {
    contentSx = { width: '100%', px: 2 };
  }

  const products = useMemo(() => arrangeProducts(directory), [directory]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle.length === 0) {
      return products;
    }
    return products.filter(
      (product) =>
        product.label.toLowerCase().includes(needle) || product.description.toLowerCase().includes(needle)
    );
  }, [products, query]);

  const visibleOrganizations = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle.length === 0) {
      return organizations;
    }
    return organizations.filter((organization) => organization.name.toLowerCase().includes(needle));
  }, [organizations, query]);

  // The legend keys the whole list. A filtered list is already the answer to a question, so
  // there is nothing left for a key to explain and it stands down until the search clears.
  const legendCategories = useMemo(() => {
    if (query.trim().length > 0) {
      return [];
    }
    const categories = directory === null ? [] : directory.categories;
    return categories.filter((category) => visible.some((product) => product.category === category.key));
  }, [directory, visible, query]);

  const arrangedShortcuts = useMemo(() => arrangeShortcuts(directory, favorites), [directory, favorites]);

  let shortcutBody = (
    <Typography variant="caption" component="p" sx={{ color: 'text.secondary', display: 'block', m: 0 }}>
      Star a product or an action to add to your favorites.
    </Typography>
  );
  if (arrangedShortcuts.length > 0) {
    shortcutBody = (
      <ShortcutChipRow
        shortcuts={arrangedShortcuts}
        onToggleFavorite={onToggleFavorite}
        onReorderFavorites={onReorderFavorites}
      />
    );
  }

  // Rendered in every state: a failed product load must never strand you in an organization.
  let organizationSection: ReactNode = null;
  if (mode === 'organizations' && organizations.length > 1 && onOrganizationChange) {
    organizationSection = (
      <OrganizationListSection
        organizations={visibleOrganizations}
        selected={selectedOrganization}
        onSelect={onOrganizationChange}
        contentSx={contentSx}
        query={query}
      />
    );
  }

  const hasJobs = directory !== null && directory.jobs.length > 0;

  // The legend rides the actions band when there is one, so the products heading only keeps
  // the room for it when it is the band actually drawing it.
  let legendBottom = 0;
  if (!hasJobs && legendCategories.length > 0) {
    legendBottom = 1.5;
  }

  // Shortcuts are products, and the organization list already marks the one you are in, so the
  // footer belongs to the product side only.
  // The organization the footer names is also the way to the rest of them.
  let organizationName: ReactNode = null;
  if (selectedOrganization) {
    organizationName = (
      <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap' }}>
        {selectedOrganization.name}
      </Typography>
    );
    if (onSearchOrganizations) {
      organizationName = (
        <ButtonBase
          onClick={onSearchOrganizations}
          aria-label={`${selectedOrganization.name} — change organization`}
          sx={{
            borderRadius: '6px',
            mx: -0.75,
            px: 0.75,
            py: 0.25,
            whiteSpace: 'nowrap',
            color: 'text.secondary',
            // Quiet at rest so it does not compete with the products; it answers to a pointer.
            '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' },
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'inherit', whiteSpace: 'nowrap' }}>
            {selectedOrganization.name}
          </Typography>
        </ButtonBase>
      );
    }
  }

  // A grid, not a wrapping row: a long shortcut list wraps inside its own column instead of
  // growing past it and pushing the organization onto a line of its own.
  // Only the open product has jobs on the body, so an empty list simply draws no band.
  let legendNode: ReactNode = null;
  if (legendCategories.length > 0) {
    legendNode = <CategoryLegend categories={legendCategories} />;
  }

  let jobsSection: ReactNode = null;
  if (directory !== null && hasJobs) {
    const openProduct = getOpenProduct(products, directory.current);
    let jobBadge = '';
    let jobCategory = 'platform';
    if (openProduct !== undefined) {
      jobBadge = openProduct.badge;
      jobCategory = openProduct.category;
    }
    jobsSection = (
      <JobsSection
        jobs={directory.jobs}
        productLabel={getOpenProductLabel(products, directory.current)}
        productBadge={jobBadge}
        productCategory={jobCategory}
        favorites={favorites}
        onToggleFavorite={onToggleFavorite}
        contentSx={contentSx}
        headingLevel={bandHeading}
        query={query}
        legend={legendNode}
      />
    );
  }

  let footerColumns = 'minmax(0, 1fr)';
  if (directory !== null && selectedOrganization && organizations.length > 1) {
    footerColumns = 'minmax(0, 1fr) auto';
  }

  let footer: ReactNode = null;
  if (mode !== 'organizations' && (directory !== null || selectedOrganization)) {
    footer = (
      <Box
        sx={{
          pt: 2,
          pb: 2.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: isPanel ? 'background.default' : 'transparent',
        }}
      >
        <Box
          sx={[
            contentSx,
            {
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: footerColumns },
              // Both headings label their own column, so they share a row: bottom-aligning the
              // columns instead would push the shorter one's heading down by its height.
              alignItems: 'flex-start',
              columnGap: 3,
              rowGap: 2,
            },
          ]}
        >
          {directory !== null ? (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                component={bandHeading}
                sx={{ color: 'text.secondary', display: 'block', mb: 1, whiteSpace: 'nowrap' }}
              >
                Shortcuts
              </Typography>
              {shortcutBody}
            </Box>
          ) : null}
          {selectedOrganization && organizations.length > 1 ? (
            <Box sx={{ justifySelf: { xs: 'start', sm: 'end' }, minWidth: 0 }}>
              <Typography
                variant="caption"
                component={bandHeading}
                sx={{ color: 'text.secondary', display: 'block', mb: 0.25, whiteSpace: 'nowrap' }}
              >
                Working in
              </Typography>
              {organizationName}
            </Box>
          ) : null}
        </Box>
      </Box>
    );
  }

  // Answered before the product branches below: picking an organization has never needed the
  // product directory, so it waits on neither its spinner nor its failure.
  if (mode === 'organizations') {
    return <Box>{organizationSection}</Box>;
  }

  if (hasFailed) {
    return (
      <Box>
        <Box sx={[contentSx, { py: 3 }]}>
          <Alert severity="error">We could not load your products. Close this and try again.</Alert>
        </Box>
        {organizationSection}
        {footer}
      </Box>
    );
  }

  if (directory === null) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress aria-label="Loading products" />
        </Box>
        {organizationSection}
        {footer}
      </Box>
    );
  }

  // On the page the heading above already names this list and spaces it, so the section repeats
  // neither. In the panel the heading is what says which list the bar is filtering.
  let sectionHeading: ReactNode = null;
  let sectionTop = 0;
  // Ruled off from the search above it exactly as the footer is ruled off below, so the sheet
  // reads as three bands. On the page the header already does the separating.
  let sectionRuleSx: Record<string, unknown> = {};
  if (isPanel) {
    sectionRuleSx = { borderTop: '1px solid', borderColor: 'divider' };
    sectionHeading = (
      <Typography variant="caption" component="h3" sx={{ color: 'text.secondary', flexShrink: 0, mr: 'auto' }}>
        Products
      </Typography>
    );
    sectionTop = 2;
  }

  return (
    <Box>
      {jobsSection}
      {/* One line, not two: the heading names the list and the legend keys it, and stacking them
          pushed the list itself a row further from the search. */}
      <Box
        sx={[
          contentSx,
          {
            pt: sectionTop,
            pb: legendBottom,
            display: 'flex',
            alignItems: 'center',
            columnGap: 3,
            rowGap: 1,
            flexWrap: 'wrap',
            ...sectionRuleSx,
          },
        ]}
      >
        {sectionHeading}
        {hasJobs ? null : legendNode}
      </Box>
      {visible.length === 0 ? (
        <Box sx={[contentSx, { pb: 2 }]}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} role="status">
            {`No products match “${query}”.`}
          </Typography>
        </Box>
      ) : (
        <Box
          data-panel-content
          sx={[
            contentSx,
            {
              display: 'grid',
              columnGap: { xs: 2, md: 4 },
              pb: 2.5,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
              },
              // Filled down one column and then the other, so the legend's order reads the way a
              // list does rather than zig-zagging across the two columns a row at a time.
              gridTemplateRows: { xs: 'none', sm: `repeat(${Math.ceil(visible.length / 2)}, auto)` },
              gridAutoFlow: { xs: 'row', sm: 'column' },
            },
          ]}
        >
          {visible.map((product) => (
            <ProductRow
              key={product.name}
              headingLevel={rowHeading}
              product={product}
              isCurrent={product.name === directory.current}
              isFavorite={favorites.includes(product.name)}
              onToggleFavorite={onToggleFavorite}
              onLaunch={onLaunch}
            />
          ))}
        </Box>
      )}
      {organizationSection}
      {footer}
    </Box>
  );
}
